import React from "react";

/**
 * Skeleton component - loading placeholder with primary-accented pulse.
 * Follows UI guidelines: rounded-lg, professional.
 * @see .cursor/ui.md
 */
const Skeleton = ({
  className = "",
  variant = "default", // default | circular
  ...props
}) => {
  const baseClasses = "animate-pulse bg-gray-200";
  const variantClasses = {
    default: "rounded",
    circular: "rounded-full",
  };

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      {...props}
    />
  );
};

/**
 * Table skeleton - matches DataGrid layout
 */
export const TableSkeleton = ({
  rows = 5,
  cols = 5,
  showHeader = true,
  className = "",
}) => (
  <div className={`overflow-hidden rounded-lg border border-gray-200 ${className}`}>
    <table className="min-w-full divide-y divide-gray-200">
      {showHeader && (
        <thead className="bg-gray-50">
          <tr>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i} className="px-6 py-4">
                <Skeleton className="h-4 w-24" />
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody className="divide-y divide-gray-200 bg-white">
        {Array.from({ length: rows }).map((_, rowIdx) => (
          <tr key={rowIdx}>
            {Array.from({ length: cols }).map((_, colIdx) => (
              <td key={colIdx} className="px-6 py-4">
                <Skeleton
                  className={`h-4 ${
                    colIdx === 0
                      ? "w-40"
                      : colIdx === cols - 1
                      ? "w-16"
                      : "w-24"
                  }`}
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Card skeleton - matches Card layout
 */
export const CardSkeleton = ({ className = "" }) => (
  <div
    className={`rounded-lg border border-gray-200 bg-white p-6 ${className}`}
  >
    <div className="flex justify-between items-center mb-4">
      <Skeleton className="h-5 w-32" />
      <Skeleton className="h-8 w-8 rounded-lg" />
    </div>
    <Skeleton className="h-4 w-full mb-2" />
    <Skeleton className="h-4 w-2/3" />
  </div>
);

/**
 * Grid skeleton - for template/card grids
 */
export const GridSkeleton = ({
  count = 8,
  viewMode = "grid", // grid | row
  className = "",
}) => (
  <div
    className={
      viewMode === "grid"
        ? `grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 ${className}`
        : `space-y-4 ${className}`
    }
  >
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="rounded-lg border border-gray-200 bg-white p-4 overflow-hidden"
      >
        <div className="flex justify-between items-center mb-2">
          <Skeleton className="h-5 w-32" />
          <div className="flex gap-2">
            <Skeleton className="h-8 w-8 rounded-lg" />
            <Skeleton className="h-8 w-8 rounded-lg" />
          </div>
        </div>
        <Skeleton className="h-3 w-full mt-2" />
        <Skeleton className="h-3 w-2/3 mt-1" />
      </div>
    ))}
  </div>
);

export default Skeleton;
